<template>
  <div>
    <v-container fluid>
      <v-card elevation="3" rounded="xl">
        <v-card-title class="py-4 px-6">
          <v-row class="w-100 ma-0" align="center">
            <v-col cols="12" md="6" class="pa-0">
              <h2 class="text-h5 font-weight-bold primary--text">RMC Deliveries</h2>
              <p class="text-body-2 grey--text ma-0">Track and manage all concrete deliveries</p>
            </v-col>
            <v-col cols="12" md="6" class="pa-0 text-right">
              <v-chip color="primary" outlined>
                <v-icon left small>mdi-truck-fast</v-icon>
                {{ count }} Deliveries
              </v-chip>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="mx-6"></v-divider>

        <v-card-subtitle class="px-6 pt-4 pb-2">
          <v-row>
            <v-col cols="12" md="3">
              <v-select v-model="filter.status" :items="statuses" label="Status" outlined rounded dense clearable @change="getData"></v-select>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <v-card-text class="pa-0">
          <section v-if="isLoading" style="height: 300px" class="d-flex align-center justify-center">
            <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
          </section>

          <v-data-table v-else :headers="headers" :items="deliveries" hide-default-footer>
            <template #[`item.sr_no`]="{ index }">
              <span class="font-weight-medium">{{ index + 1 }}</span>
            </template>

            <template #[`item.order`]="{ item }">
              <span class="font-weight-medium primary--text">#RMC-{{ item.id }}</span>
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip :color="statusColor(item.status)" small dark>{{ statusLabel(item.status) }}</v-chip>
            </template>

            <template #[`item.plant`]="{ item }">
              <div v-if="item.plant">
                <div class="font-weight-medium">{{ item.plant.name }}</div>
                <div class="text-caption grey--text">{{ item.plant.city }}</div>
              </div>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.builder`]="{ item }">
              <div v-if="item.user">
                <div>{{ item.user.first_name || item.user.user_name }}</div>
                <div class="text-caption grey--text">{{ item.user.phone || '' }}</div>
              </div>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.capacity`]="{ item }">
              {{ item.capacity_cubic_m ? item.capacity_cubic_m + ' m³' : '—' }}
            </template>

            <template #[`item.amount`]="{ item }">
              <span v-if="item.total_amount" class="font-weight-medium">₹{{ formatNumber(item.total_amount) }}</span>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.payment`]="{ item }">
              <div>
                <v-chip x-small :color="paymentColor(item.payment_mode)" outlined>{{ paymentLabel(item.payment_mode) }}</v-chip>
                <v-chip v-if="item.payment_mode === 'cod'" x-small :color="item.cod_payment_received ? 'success' : 'warning'" class="ml-1" dark>
                  {{ item.cod_payment_received ? 'Paid' : 'Pending' }}
                </v-chip>
              </div>
            </template>

            <template #[`item.address`]="{ item }">
              <div v-if="item.delivery_address" class="text-caption" style="max-width:200px">
                <v-icon x-small color="primary">mdi-map-marker</v-icon>
                {{ item.delivery_address }}
              </div>
              <span v-else class="grey--text text-caption">—</span>
            </template>

            <template #[`item.eta`]="{ item }">
              <span v-if="item.estimated_time" class="success--text text-caption font-weight-medium">{{ item.estimated_time }}</span>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn x-small color="primary" outlined @click="openDetail(item)">
                <v-icon x-small left>mdi-eye</v-icon> View
              </v-btn>
            </template>

            <template #no-data>
              <div class="text-center py-12">
                <v-icon size="60" color="grey lighten-3">mdi-truck-fast</v-icon>
                <h3 class="grey--text mt-4">No Deliveries Found</h3>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Delivery Detail Dialog -->
    <v-dialog v-model="showDetail" max-width="560">
      <v-card rounded="lg" v-if="selected">
        <v-card-title class="pa-6 pb-3">
          <v-icon left color="primary">mdi-truck-fast</v-icon>
          Delivery #RMC-{{ selected.id }}
          <v-spacer></v-spacer>
          <v-chip :color="statusColor(selected.status)" small dark>{{ statusLabel(selected.status) }}</v-chip>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="px-6 pt-4 pb-2">
          <!-- Status Timeline -->
          <p class="text-caption grey--text mb-2 font-weight-bold text-uppercase">Timeline</p>
          <div class="d-flex align-center mb-4" style="overflow-x:auto; gap:4px">
            <template v-for="(s, i) in statusOrder">
              <v-chip :key="s" :color="isStatusReached(s) ? statusColor(s) : 'grey lighten-3'" x-small :dark="isStatusReached(s)" :outlined="!isStatusReached(s)">{{ statusLabel(s) }}</v-chip>
              <v-icon v-if="i < statusOrder.length - 1" :key="'arrow-'+i" x-small color="grey">mdi-chevron-right</v-icon>
            </template>
          </div>

          <v-divider class="mb-4"></v-divider>

          <v-row dense>
            <v-col cols="6">
              <p class="text-caption grey--text mb-0">Plant</p>
              <p class="font-weight-medium mb-3">{{ selected.plant ? selected.plant.name : '—' }}</p>
            </v-col>
            <v-col cols="6">
              <p class="text-caption grey--text mb-0">Builder</p>
              <p class="font-weight-medium mb-3">{{ selected.user ? (selected.user.first_name || selected.user.user_name) : '—' }}</p>
            </v-col>
            <v-col cols="6">
              <p class="text-caption grey--text mb-0">Capacity</p>
              <p class="font-weight-medium mb-3">{{ selected.capacity_cubic_m ? selected.capacity_cubic_m + ' m³' : '—' }}</p>
            </v-col>
            <v-col cols="6">
              <p class="text-caption grey--text mb-0">Amount</p>
              <p class="font-weight-medium mb-3">{{ selected.total_amount ? '₹' + formatNumber(selected.total_amount) : '—' }}</p>
            </v-col>
            <v-col cols="6">
              <p class="text-caption grey--text mb-0">Payment Mode</p>
              <p class="font-weight-medium mb-3">{{ paymentLabel(selected.payment_mode) }}</p>
            </v-col>
            <v-col cols="6" v-if="selected.payment_mode === 'cod'">
              <p class="text-caption grey--text mb-0">COD Status</p>
              <v-chip x-small :color="selected.cod_payment_received ? 'success' : 'warning'" dark class="mb-3">
                {{ selected.cod_payment_received ? 'Paid' : 'Pending' }}
              </v-chip>
            </v-col>
            <v-col cols="6" v-if="selected.estimated_time">
              <p class="text-caption grey--text mb-0">ETA</p>
              <p class="font-weight-medium success--text mb-3">{{ selected.estimated_time }}</p>
            </v-col>
            <v-col cols="6" v-if="selected.transit_mixer_name">
              <p class="text-caption grey--text mb-0">Transit Mixer</p>
              <p class="font-weight-medium mb-3">{{ selected.transit_mixer_name }}</p>
            </v-col>
            <v-col cols="12" v-if="selected.delivery_address">
              <p class="text-caption grey--text mb-0">Delivery Address</p>
              <p class="font-weight-medium mb-3">
                <v-icon small color="primary">mdi-map-marker</v-icon>
                {{ selected.delivery_address }}
              </p>
            </v-col>
            <v-col cols="6" v-if="selected.dispatched_at">
              <p class="text-caption grey--text mb-0">Dispatched At</p>
              <p class="font-weight-medium mb-3">{{ selected.dispatched_at.substring(0, 16) }}</p>
            </v-col>
            <v-col cols="6" v-if="selected.delivered_at">
              <p class="text-caption grey--text mb-0">Delivered At</p>
              <p class="font-weight-medium success--text mb-3">{{ selected.delivered_at.substring(0, 16) }}</p>
            </v-col>
            <v-col cols="12" v-if="selected.failure_reason">
              <p class="text-caption grey--text mb-0">Failure Reason</p>
              <p class="error--text font-weight-medium mb-3">{{ selected.failure_reason }}</p>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn text @click="showDetail = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "RmcDeliveriesPage",
  data() {
    return {
      deliveries: [],
      count: 0,
      isLoading: false,
      showDetail: false,
      selected: null,
      filter: { status: "" },
      statuses: ["placed", "confirmed", "loading", "dispatched", "in_transit", "delivered", "failed", "re_delivery"],
      statusOrder: ["placed", "confirmed", "loading", "dispatched", "in_transit", "delivered"],
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: "50px" },
        { text: "Order", value: "order", width: "100px" },
        { text: "Status", value: "status", width: "130px" },
        { text: "Plant", value: "plant", width: "160px" },
        { text: "Builder", value: "builder", width: "150px" },
        { text: "Capacity", value: "capacity", width: "100px" },
        { text: "Amount", value: "amount", width: "110px" },
        { text: "Payment", value: "payment", width: "140px" },
        { text: "Address", value: "address", width: "200px" },
        { text: "ETA", value: "eta", width: "100px" },
        { text: "", value: "actions", sortable: false, width: "80px" },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filter.status) params.status = this.filter.status;
        const query = new URLSearchParams(params);
        const response = await axios.get(`rmc_deliveries?${query}`);
        this.deliveries = response.data.data || [];
        this.count = response.data.count || 0;
      } catch (e) {
        console.error("Error fetching deliveries:", e);
      }
      this.isLoading = false;
    },
    openDetail(item) {
      this.selected = item;
      this.showDetail = true;
    },
    isStatusReached(status) {
      if (!this.selected) return false;
      const currentIdx = this.statusOrder.indexOf(this.selected.status);
      const targetIdx = this.statusOrder.indexOf(status);
      return currentIdx >= 0 && targetIdx >= 0 && targetIdx <= currentIdx;
    },
    statusLabel(status) {
      const labels = { placed: "Placed", confirmed: "Confirmed", loading: "Loading", dispatched: "Dispatched", in_transit: "In Transit", delivered: "Delivered", failed: "Failed", re_delivery: "Re-Delivery" };
      return labels[status] || status;
    },
    statusColor(status) {
      const colors = { placed: "blue", confirmed: "success", loading: "orange", dispatched: "primary", in_transit: "purple", delivered: "success", failed: "error", re_delivery: "warning" };
      return colors[status] || "grey";
    },
    paymentLabel(mode) {
      const labels = { bank_transfer: "Bank Transfer", cod: "COD", credit: "Credit" };
      return labels[mode] || mode || "—";
    },
    paymentColor(mode) {
      const colors = { bank_transfer: "blue", cod: "orange", credit: "purple" };
      return colors[mode] || "grey";
    },
    formatNumber(num) {
      return new Intl.NumberFormat("en-IN").format(parseFloat(num) || 0);
    },
  },
};
</script>
